import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  InviteUserRequest,
  UpdateUserRequest,
  UserDto,
} from 'src/api/FiretrolApiClient';
import { userApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

function seedUser(
  qc: ReturnType<typeof useQueryClient>,
  user: UserDto | undefined,
) {
  if (!user?.id) return;
  qc.setQueryData(queryKeys.organization.adminUserDetail(user.id), user);
  qc.setQueryData(queryKeys.users.detail(user.id), user);
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: InviteUserRequest) =>
      userApiClient.inviteUser(request),
    meta: { suppressErrorToast: true },
    onSuccess: (user) => seedUser(qc, user),
    onSettled: () => invalidateFor('user.invite', qc),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateUserRequest }) =>
      userApiClient.updateUser(id, request),
    meta: { suppressErrorToast: true },
    onSuccess: (user) => seedUser(qc, user),
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      userApiClient.updateUserStatus(id, { status }),
    onSuccess: (user) => seedUser(qc, user),
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useArchiveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => userApiClient.archiveUser(id),
    meta: { suppressErrorToast: true },
    onSuccess: (user) => seedUser(qc, user),
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useUnarchiveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => userApiClient.unarchiveUser(id),
    meta: { suppressErrorToast: true },
    onSuccess: (user) => seedUser(qc, user),
    onSettled: () => invalidateFor('user.update', qc),
  });
}
